import { useEffect, useRef, useState, type CSSProperties } from "react";
import { MessageCircle } from "lucide-react";
import { FaInstagram, FaLinkedinIn, FaWhatsapp } from "react-icons/fa";
import { SOCIAL } from "@/data/contact";

// Radius of the arc the options fan out on, in px.
const RADIUS = 88;

// Brand colours, so each option is recognisable at a glance. `angle` places
// it on the arc (degrees, counter-clockwise from pointing right): together
// they make a half-circle tilted towards the top left of the button.
const OPTIONS = [
  {
    href: SOCIAL.whatsapp,
    label: "Chat with HBT Integrisys on WhatsApp",
    Icon: FaWhatsapp,
    background: "#25D366",
    angle: 100,
  },
  {
    href: SOCIAL.instagram,
    label: "HBT Integrisys on Instagram",
    Icon: FaInstagram,
    background: "linear-gradient(45deg, #feda75, #d62976 55%, #4f5bd5)",
    angle: 145,
  },
  {
    href: SOCIAL.linkedin,
    label: "HBT Integrisys on LinkedIn",
    Icon: FaLinkedinIn,
    background: "#0A66C2",
    angle: 190,
  },
];

/**
 * Chat button fixed to the bottom-right corner of every page. Hovering it
 * (or tapping / focusing it) pops WhatsApp, Instagram and LinkedIn out
 * along an arc (`.fc-*` in index.css).
 */
export function FloatingContact() {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  // Closing waits a moment after the pointer leaves, so moving from the
  // button out to an option doesn't close the menu on the way.
  const closeTimer = useRef<ReturnType<typeof setTimeout>>(undefined);
  const show = () => {
    clearTimeout(closeTimer.current);
    setOpen(true);
  };
  const hideSoon = () => {
    clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setOpen(false), 300);
  };
  useEffect(() => () => clearTimeout(closeTimer.current), []);
  // A mouse has already opened the menu by hovering, so its click shouldn't
  // close it again; taps and the keyboard toggle it.
  const pointerType = useRef("");

  // Tapping anywhere else closes it (touch screens have no pointer leave).
  useEffect(() => {
    if (!open) return;
    const onDown = (e: PointerEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("pointerdown", onDown);
    return () => document.removeEventListener("pointerdown", onDown);
  }, [open]);

  return (
    <div
      ref={rootRef}
      className={open ? "fc fc-open" : "fc"}
      onPointerEnter={(e) => e.pointerType === "mouse" && show()}
      onPointerLeave={(e) => e.pointerType === "mouse" && hideSoon()}
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget)) setOpen(false);
      }}
      onKeyDown={(e) => {
        if (e.key === "Escape") setOpen(false);
      }}
    >
      <ul id="contact-options" className="fc-options" aria-label="Contact us">
        {OPTIONS.map(({ href, label, Icon, background, angle }, idx) => {
          const rad = (angle * Math.PI) / 180;
          const style = {
            background,
            "--fc-x": `${Math.round(Math.cos(rad) * RADIUS)}px`,
            "--fc-y": `${Math.round(-Math.sin(rad) * RADIUS)}px`,
            "--fc-delay": `${idx * 60}ms`,
          } as CSSProperties;
          return (
            <li key={href}>
              <a
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={label}
                title={label}
                tabIndex={open ? 0 : -1}
                className="fc-option"
                style={style}
              >
                <Icon aria-hidden="true" />
              </a>
            </li>
          );
        })}
      </ul>

      <button
        type="button"
        className="fc-button"
        aria-label={open ? "Hide contact options" : "Contact us"}
        aria-expanded={open}
        aria-controls="contact-options"
        onPointerDown={(e) => {
          pointerType.current = e.pointerType;
        }}
        onClick={() => {
          const byMouse = pointerType.current === "mouse";
          pointerType.current = "";
          setOpen((o) => (byMouse ? true : !o));
        }}
      >
        <MessageCircle aria-hidden="true" />
      </button>
    </div>
  );
}
